/*global setTimeout, define */

//	+-----------------------------------------------+
//	| DfPE view objects                             |
//	+-----------------------------------------------+

/**
 * @file views.js
 * @desc DfPE view objects: This file contains the view objects that
 * render and control any kind of data display.
 * @author Geoffrey Pursell
 */

define('cardboard/databoxes',[
  "require",
  // libraries
  "jquery",
  "underscore",
  "underscore.string",
  "backbone",
  "hogan",
  "typeahead",
  "bowser",
  "rsvp",

  // app modules
  "cardboard/models",
  "cardboard/collections",
  "commonDataSources",
  "cardboard/baseViews",
  "cardboard/chartViews",
  "cardboard/pluginHelper",
  "cardboard/util",
  "cardboard/filterViews",

  // templates and text assets
  "text!templates/databox.html!strip",
  "text!templates/dataBoxConfig.html!strip",
  "text!templates/dataBoxPaneEnhanced.html!strip",
], function (
  require,
  $,
  _,
  _s,
  Backbone,
  Hogan,
  Typeahead,
  Bowser,
  RSVP,
  Models,
  Collections,
  CommonDataSources,
  Views,
  Charts,
  PluginHelper,
  Util,
  FilterViews,
  dataBoxTemplate,
  dataBoxConfigTemplate,
  dataBoxPaneTemplate
) {
  console.log("MODULE: cardboard/databoxes");

  /**
   * @exports cardboard/databoxes
   * @desc DfPE databox view objects: everything to do with data box views
   */
  var DataBoxes = {},
    Promise = RSVP.Promise;
  var ct = 0;

  // compile our templates, if they're not already!

  if (typeof dataBoxTemplate === "string") {
    dataBoxTemplate = Hogan.compile(dataBoxTemplate);
  }

  if (typeof dataBoxPaneTemplate === "string") {
    dataBoxPaneTemplate = Hogan.compile(dataBoxPaneTemplate);
  }

  if (typeof dataBoxConfigTemplate === "string") {
    dataBoxConfigTemplate = Hogan.compile(dataBoxConfigTemplate);
  }

  /**
   * @class module:cardboard/databoxes.DataBoxView
   * @augments Views.BaseView
   * @classdesc This renders a single data box. It's responsible for
   * rendering itself, its configuration (!), and managing one or
   * more sub-views, notably a collection of charts and tables.
   * @param {Object} options a hash of options to pass to the
   * backbone view. {@link http://backbonejs.org/#View-constructor}
   */

  DataBoxes.DataBoxView = Views.BaseView.extend(
    /** @lends module:cardboard/databoxes.DataBoxView.prototype */
    {
      model: null,
      boxNumber: 1, // hard-coded for now

      /** the HTML template string we use to construct the view's
       * wrapper DOM element. this gets replaced by an element maker
       * function at runtime.
       * @type String */
      wrapperTemplate: dataBoxTemplate,

      /** declarative events
       * @type Object */
      events: {
        "click .closeButton": "onCloseButtonClicked",
        "click .content .mask": "closeDrawer",
        "click .reloadButton": "onReloadButtonClicked",
      },

      /** custom declarative events */
      globalEvents: {
        drawersShouldClose: "onDrawersShouldClose",
      },

      /** constructor
       * @param {DOMElement | jQuery | String} container a box into which the new
       * DataBoxView should go */

      initialize: function (dataSources, filterSources, controller) {
        /** the list of DataBoxPane view objects for this databox.
         * @type Array */
        this.paneViews = [];

        /** the array of sub-views
         * @type Array */
        this.dataElements = [];

        // experimental: save a ref to the controller object that created
        // us. this could cause a circular reference, let's find out
        this.controller = controller;

        /** the containing box for all databoxes. needs to be set
         * late, at init time, 'cuz it's DOM
         * @type jQuery */
        this.$container = $("#dataBoxes");

        this.createWrapperElement(); // create container and render into it
        this.$content = this.$el.children(".content"); // save a reference to the content area, we'll want it later

        this.attachGlobalEvents();
      },
      destroy: function () {
        // just to be safe
        delete this.controller;

        if (this.drawerView) {
          this.drawerView.destroy();
        }
        Util.Data.popEach(this.paneViews, function (paneView) {
          paneView.destroy();
        });
        this.undelegateEvents();
        this.stopListening();
        this.remove();
      },

      /** render the view */
      render: function (reconstructingParams) {
        // attach the created DOM element to the right place
        var $adder = this.$container.find(".dataBoxAdder"),
          dataBoxCount = this.$container.children(".dataBox").length;

        if ($adder.length) {
          this.$el.insertBefore($adder);
        } else {
          this.$container.append(this.$el);
        }

        //set data-dynamic text for Cardstock
        //this.$el.attr("data-dynamiccontent", PluginHelper.getCurrentPluginOptions().dynamicTextDomain);
        //    this.$el.attr("data-dynamiccontent", PluginHelper.getCurrentPluginOptions().dynamicTextDomain);

        // create and render the drawer
        this.createConfigDrawerView();

        // create the pane views
        this.createPaneViews(reconstructingParams);

        // attach events
        this.listenTo(
          this.drawerView,
          "filterMenusRendered",
          this.updateSummary.bind(this)
        );
        this.delegateEvents();

        // adjust z-index so an open drawer isn't overlapped by following
        // databoxes in their natural z-order
        this.$el.css("z-index", 50 - dataBoxCount);

        return this;
      },

      /** create the drawer sub-view, and render it */
      createConfigDrawerView: function () {
        // if we're re-rendering the drawer view, we need to
        // dispose of resources around the old one.
        if (this.drawerView) {
          this.stopListening(this.drawerView);
          this.drawerView.destroy();
        }

        // make the drawer control
        this.drawerView = new DataBoxes.ConfigDrawerView({
          externalToggles: [
            this.$("header > .configureButton"),
            this.$("header > .text"),
          ],
          el: this.$(".drawer"),
          filterSources: this.getFilterSources(),
          parentView: this,
        });

        this.drawerView.render();

        // EVENTS
        // all non-global events that get fired by the
        // config drawer should also get fired by this. event
        // proxying!
        this.repeatEventsFrom(this.drawerView);
        this.listenTo(this, "filterChanged", this.onFilterChanged);
        this.listenTo(this, "beforeDrawerOpen", this.beforeDrawerOpen);
        this.listenTo(this, "beforeDrawerClose", this.beforeDrawerClose);
        this.listenTo(this, "drawerClosed", this.onDrawerClosed);

        // RENDER the settings summary ASAP
        if (this.drawerView.loaded) {
          this.updateSummary();
        }
      },

      /** create the paneViews that will sit in this databox
       * @param {Object} reconstructingParams - needed so that we know which
       * ones are going to be open on first render*/
      createPaneViews: function (reconstructingParams) {
        //   this.controller.view.setFY(reconstructingParams.FISCAL_YEAR);
        var openPanesArray = null;
        if (reconstructingParams) {
          openPanesArray = Util.Data.bitmaskToPositionArray(
            reconstructingParams.p
          );
        }

        this.destroyPaneViews();

        // intitialize each pane view object
        _(PluginHelper.getCurrentPlugin().panes)
          .filter(this.paneShouldBeRendered, this)
          .each(function (paneConfigObject, i) {
            // visibility policy is this: first pane is open by default,
            // unless the pane config's openByDefault says it shouldn't
            // be; all others are closed by default unless openByDefault
            // says they shouldn't be
            var openByDefault =
              (i === 0 && paneConfigObject.openByDefault !== false) ||
              paneConfigObject.openByDefault;

            this.addPaneView(
              paneConfigObject,
              (!reconstructingParams && openByDefault) ||
                (reconstructingParams && _(openPanesArray).contains(i))
            );
          }, this);
        //   this.controller.view.setFY(reconstructingParams.FISCAL_YEAR);
        //this.controller.view.setFilterValue(FISCAL_YEAR, reconstructingParams.FISCAL_YEAR);
        this.controller.view.updateFilterMenus(reconstructingParams);
        //   this.view.updateFilterMenus();
      },
      destroyPaneViews: function () {
        Util.Data.popEach(this.paneViews, function (paneView) {
          paneView.destroy();
        });
      },

      // ACTIONS AND UPDATERS

      /** add a single pane view
       * @param {Object} paneConfigObject the config JSON that defines the pane
       * @param {Number} the index of the given pane; good for calling this
       * as an iterator
       * @param {DOMElement} paneToReplace an optional existing pane that we
       * want to replace*/
      addPaneView: function (paneConfigObject, openByDefault, paneToReplace) {
        var pv = new DataBoxes.DataBoxPaneView(paneConfigObject, this);
        this.paneViews.push(pv);
        pv.render(openByDefault, paneToReplace); // first param is whether the pane
        // should be open by default
        return pv;
      },

      /** replace a single pane view
       * @param {Object} existingPaneView the pane to be replaced
       * @param {Object} newPaneConfigObject the config JSON that defines the new pane
       * as an iterator*/
      replacePaneView: function (existingPaneView, newPaneConfigObject) {
        var paneIndex = _.indexOf(this.paneViews, existingPaneView),
          oldPaneElement = existingPaneView.el,
          currentlyOpen = existingPaneView.isOpen();

        // deallocate, create new pane, somehow render into a new
        // pane in the same location, or replace contents of
        // existing pane, maybe remove old pane from DOM
        this.paneViews[paneIndex] = this.addPaneView(
          newPaneConfigObject,
          currentlyOpen,
          oldPaneElement
        );

        existingPaneView.destroy();
      },

      /** this is both how the drawer first gets created, and how
       * it's updated at the request of the controller whenever the
       * report changes */
      updateConfigDrawerView: function () {
        if (!this.drawerView) {
          this.createConfigDrawerView();
        }
        this.drawerView.update();
        this.updateSummary();
      },

      /** this is to check the "when" conditions of each pane view (the
       * controller calls this when the filter selection has changed) and
       * delete/render them if necessary.  NOTE: this is debounced to prevent
       * doing this several times in rapid succession when the org changes.*/
      updatePaneViews: function () {
        // go find all the panes that shouldn't be shown, and then for each,
        // find another one in its group that should be shown instead, and
        // swap it in.
        _(this.paneViews)
          .reject(this.paneShouldBeRendered, this)
          .each(this.swapPaneView, this);
      },

      swapPaneView: function (paneView) {
        var newPaneConfigObject = this.findReplacementPaneConfig(paneView);

        if (newPaneConfigObject) {
          this.replacePaneView(paneView, newPaneConfigObject);
        }
      },

      /** add some new thing to our content div
       * @param {Element | jQuery} passedNewThing the thing to add
       * @param {Element | jQuery} optional; an existing element to replace */
      addToContent: function (passedNewThing, thingToReplace) {
        var $newThing = $(passedNewThing),
          $thingToReplace = $(thingToReplace);

        if (thingToReplace) {
          $thingToReplace.replaceWith($newThing);
        } else {
          this.$content.append($newThing);
        }
      },
      updateSummary: function () {
        // if there's an org chosen, we should update the
        // summary. Otherwise...no.
        var demographicsPresent = false;
        if (this.getOrg() || ct++ > 0) {
          var summaryObj = this.drawerView.getSummaryObject();

          $.each(summaryObj.entries, function (index, value) {
            if (value.indexOf("Demographics") > -1) demographicsPresent = true;
          });
          if (!demographicsPresent)
            summaryObj.entries.push(
              "<span class='label'>Demographics</span>: All Students"
            );

          if (summaryObj) {
            this.$("header .title").html(summaryObj.orgName);
            this.$("header .parent").html(summaryObj.parentName || "");
            this.$("header .summary").html(summaryObj.entries.join("  "));
          }
        }
      },

      /** if the plugin has asked that there be no default org set (no
       * auto-load of statewide data), it may also request that there be
       * placeholder text instead of the word "Statewide" in the data box
       * header. */
      setPlaceholderSummary: function () {
        var po = PluginHelper.getCurrentPluginOptions();

        if (po.noDefaultOrg && po.orgPlaceholderText) {
          this.$("header .title").html(po.orgPlaceholderText);
        }
      },

      /** proxy function to drawer -> org finder */
      splitOrgID: function () {
        this.drawerView.splitOrgID();
      },

      /** tell all data panes to mask their views */
      maskViewsForLoading: function () {
        _(this.paneViews).invoke("maskViewsForLoading");
      },

      /** dim the whole content area, for when the drawer is open */
      maskContent: function () {
        Util.UI.maskElement(this.$content);
      },
      /** un-dim the whole content area, for when the drawer closes */
      unmaskContent: function () {
        Util.UI.unmaskElement(this.$content);
      },
      /** dim the whole content area, for when the drawer is open */
      maskContentForLoading: function () {
        Util.UI.maskForLoading(this.$content);
      },
      /** un-dim the whole content area, for when the drawer closes */
      unmaskContentForLoading: function () {
        Util.UI.unmaskForLoading(this.$content);
      },
      setPaneState: function (openPanesArray) {
        this.suppressPaneEvents = true;

        // iterating over the list of open panes made it hard to close them
        // when the user is navigating the browser history with
        // back/forward, so now we iterate over the list of paneViews
        // instead.
        _(this.paneViews).each(function (pane, index) {
          // if (pane.isErrorPane()) { return; }
          if (openPanesArray.indexOf(index) !== -1) {
            pane.open();
          } else {
            pane.close();
          }
        });

        this.suppressPaneEvents = false;
      },

      // PROXY INTERFACES
      // these are just to hide the internal implementation from
      // outside callers (like the controller)

      setFilterState: function (selectionStateToRestore) {
        this.drawerView.setFilterState(selectionStateToRestore);
      },
      updateFilterMenus: function (selectionStateToRestore) {
        this.drawerView.updateFilterMenus(selectionStateToRestore);
      },

      updateDemoCats: function (selectionStateToRestore) {
        this.drawerView.updateDemoCats(selectionStateToRestore);
      },
      getFilterValue: function (name) {
        return this.drawerView.getFilterValue(name);
      },
      setFilterValue: function (name, value) {
        this.drawerView.setFilterValue(name, value);
      },
      getOrg: function () {
        if (this.drawerView) {
          return this.drawerView.getOrg();
        } else {
          return null;
        }
      },
      setOrg: function (orgObject) {
        if (this.drawerView) {
          this.drawerView.setOrg(orgObject);
        }
      },
      setFY: function (fY) {
        if (this.drawerView) {
          this.drawerView.setFY(fY);
        }
      },
      getConfigSelections: function () {
        return this.drawerView.getSelections();
      },
      closeDrawer: function () {
        this.drawerView.close();
      },
      openDrawer: function () {
        this.drawerView.open();
      },

      // INFO METHODS
      getConfigParams: function () {
        return this.drawerView.getParams();
      },
      getErrorPaneView: function () {
        return _(this.paneViews).find(function (pv) {
          return pv.options.type === "errorPane";
        });
      },
      /** since we're loosely bound to our DataBoxController, use this method
       * to retrieve it. WARNING: this only works *after* the data box has
       * been instatiated. We can get here from inside the DataBoxView's
       * constructor lots of times where it doesn't work.
       * UPDATE: we're no longer loosely bound, just do this.
       * @returns {object} this data box's DataBoxController object
       */
      getController: function () {
        // return window.workspace.getDataBoxControllerFromView(this);
        return this.controller;
      },
      /** return the state of pane visibility as an array of integer "bits",
       * zero or one */
      getPaneState: function () {
        var openOnes = [];
        _(this.paneViews)
          .reject(function (pane) {
            return pane.isErrorPane();
          })
          .each(function (pane, index) {
            if (pane.isOpen()) {
              openOnes.push(index);
            }
          });

        return Util.Data.positionArrayToBitmask(openOnes);
      },
      getDataSources: function () {
        return this.controller.dataSources;
      },
      getFilterSources: function () {
        return this.controller.filterSources;
      },

      /** evaluate a pane's condition against the current settings
       * @param {Object} condition the condition from the pane config
       * @returns {Boolean} the truth value of the condition */

      evaluatePaneCondition: function (condition) {
        return Util.evaluateFilterCondition(
          condition,
          this.drawerView.getParams()
        );
      },

      /** method to help others not care if they get passed a pane view object
       * or a pane view config object */
      getPaneConfig: function (paneViewOrConfig) {
        return _.isFunction(paneViewOrConfig.getConfigObject)
          ? paneViewOrConfig.getConfigObject()
          : paneViewOrConfig;
      },
      /** get the where condition from a pane, or its config object, and
       * evaluate it, return its truth value. */
      paneShouldBeRendered: function (pane) {
        // we can be passed either a real pane view, or a pane view config object
        var paneConfigObject = this.getPaneConfig(pane),
          condition = paneConfigObject.visibleWhen;

        // if there are no filters it's always true.
        if (!this.getFilterSources().filters) {
          return true;
        }

        if (condition) {
          if (!condition.filter) {
            throw "missing filter property in pane config object";
          }
          return this.evaluatePaneCondition(condition);
        } else {
          // no when blob --> always return true
          return true;
        }
        // if the filters haven't loaded yet, we have to depend on the pane
        // being marked up as visible by default.
        if (!this.getFilterSources().filters.isLoaded()) {
          return (
            paneConfigObject.visibleByDefault ||
            _.isUndefined(paneConfigObject.visibleByDefault)
          );
        }
      },
      findReplacementPaneConfig: function (deadPaneView) {
        var groupID = deadPaneView.getConfigObject().groupID,
          replacementPaneConfig = _(PluginHelper.getCurrentPlugin().panes)
            .where({
              groupID: groupID,
            })
            .find(function (paneConfigObject) {
              return this.evaluatePaneCondition(paneConfigObject.visibleWhen);
            }, this);

        if (!replacementPaneConfig) {
          throw "Pane swap error: no matching pane found in group " + groupID;
        }

        return replacementPaneConfig;
      },

      // EVENT HANDLERS
      // do stuff with only visual consequences. all other duties are handled
      // by the controller

      onFilterChanged: function () {
        // the controller now triggers the summary update to avoid
        // order-of-execution bugs
        //                    this.$el.focus();
      },
      onSettingsLoaded: function () {
        this.updateSummary();
      },
      beforeDrawerOpen: function () {
        this.maskContent();
      },
      beforeDrawerClose: function () {
        this.unmaskContent();
      },
      onDrawerClosed: function () {
        // this.$el.removeClass("drawerOpen").addClass("drawerClosed");
      },
      // DOM events
      onCloseButtonClicked: function () {
        this.trigger("destroy", {
          removeNow: true,
        });
      },
      onDrawersShouldClose: function () {
        // close ours
        this.drawerView.close();
      },
      onReloadButtonClicked: function () {
        this.controller.loadReportDataSources();
      },
    }
  );

  /**
   * @class module:cardboard/databoxes.ConfigDrawerView
   * @augments Views.BaseView
   * @classdesc This renders a single pane inside a data box. It's
   * responsible for rendering itself, and probably some
   * controller-ish duties, for simplicity
   */
  DataBoxes.ConfigDrawerView = Views.BaseView.extend(
    /** @lends module:cardboard/databoxes.ConfigDrawerView.prototype */ {
      /** declarative events. empty for now
       * @type Object */
      events: {
        // "click .overlay": "onOverlayClicked",
        keypress: "onKeyPressed",
      },

      contentTemplate: dataBoxConfigTemplate,

      method: "top",
      loaded: false,
      extraPixels: 12, // # of pixels needed to get the shadow out of your face
      animationDuration: 200, // in ms -- this is only for "top" method

      initialize: function (optionsIn) {
        // capture passed options
        this.options = _.extend(
          {
            drawerElement: null,
            externalToggles: [],
          },
          optionsIn
        );
        this.dataBoxView = optionsIn.parentView;
        //      window.count = 0;

        this.filterMenusViews = [];

        // set up the invisible div that expands to reveal the
        // contents of the drawer, including the handle
        // this.$expander = this.$(".overlay");
        // this.$expander.height(0);

        // and the auto-height box that contains the content and sizes itself to match
        this.$heightBox = this.$(".heightBox");

        // if we can, lean on the GPU for the animation
        // the css 3d method is buggy in old Safaris, dont' use it
        if (
          !(Bowser.safari && Bowser.version < 6) &&
          $("html").is(".csstransforms3d")
        ) {
          this.method = "translate3d";
          this.$el.addClass("method_translate3d");
        } else {
          this.method = "top";
          this.$el.addClass("method_top");
        }
      },

      /** attach event handlers for drawer toggles */
      attachToggleEventHandlers: function () {
        this.toggles = $(this.options.externalToggles)
          .map(function () {
            return $(this).toArray();
          })
          .add(this.$(".handle"))
          .on("click", this.onTogglePressed.bind(this))
          .on("keydown", this.onTogglePressed.bind(this))
          .on("touchend", this.onToggleTouchEnd.bind(this));
      },

      destroy: function () {
        // this.$expander.off("webkitTransitionEnd otransitionend oTransitionEnd msTransitionEnd transitionend");
        $(this.options.externalToggles)
          .map(function () {
            return $(this).toArray();
          })
          .add(this.$(".handle"))
          .off("vclick")
          .off("click");

        this.undelegateEvents();
        this.stopListening();

        if (this.orgFinderView) {
          this.orgFinderView.destroy();
        }
        if (this.hasFilterMenus()) {
          _(this.filterMenusViews).invoke("destroy");
        }
        if (this.demoCatsView) {
          this.demoCatsView.destroy();
        }
      },

      render: function () {
        // don't render this at all until we know we've got entity
        // AND filter data loaded up
        var filterSources = this.dataBoxView.getFilterSources(),
          filtersModel = filterSources.filters,
          orgsModel = CommonDataSources.entities || filterSources.orgs;

        if (!orgsModel) {
          var errorMessage = "ConfigDrawerView error: missing org list.";
          console.log(errorMessage);
          throw errorMessage;
        }

        var requests = window.fastOrgs
            ? [
                CommonDataSources.fastSchools.requestDonePromise,
                CommonDataSources.fastDistricts.requestDonePromise,
              ]
            : [orgsModel.requestDonePromise],
          $content = (this.$content = this.$(".content")),
          dataView = {
            boxNumber: this.dataBoxView.boxNumber,
            reportConfig: PluginHelper.getCurrentPluginOptions(),
          },
          requestPromise = Promise.all(requests);

        if (filtersModel) {
          requests.push(filtersModel.requestDonePromise);
        }

        // render the whole drawer at once
        $content.html(this.contentTemplate.render(dataView));

        this.createOrgFinderView();
        this.createFilterMenusViews();
        this.createDemoCatsView();

        this.listenTo(
          this.orgFinderView,
          "escKeyPressed",
          this.onEscKeyPressed
        );

        requestPromise
          .then(
            function () {
              // we wait until now to wire up the events for drawer
              // toggles -- don't want people seeing an empty drawer
              this.attachToggleEventHandlers();
              this.createDebugToolbar();

              // let people know we're done here
              this.loaded = true;
            }.bind(this)
          )
          .then(null, Util.reportPromiseException);
      },

      // creation of sub-views

      createOrgFinderView: function () {
        if (this.orgFinderView) {
          this.orgFinderView.destroy();
        }
        this.orgFinderView = new FilterViews.OrgFinderView({
          parentView: this,
          el: this.$content.find(".orgFinder"),
          model:
            CommonDataSources.entities ||
            this.dataBoxView.getFilterSources().orgs,
        });
        this.repeatEventsFrom(this.orgFinderView);
      },
      createFilterMenusViews: function () {
        var dataSources = _.compact([
          this.dataBoxView.getFilterSources().filters,
          this.dataBoxView.getFilterSources().filters2,
          this.dataBoxView.getFilterSources().filters3,
          this.dataBoxView.getFilterSources().filters4,
        ]);

        // destroy any old ones, and clean out the DOM containers for any
        // filter sets that don't exist anymore
        _(this.filterMenusViews).invoke("destroy");
        this.filterMenusViews = [];
        this.$content
          .find(".filters, .filters2, .filters3, .filters4")
          .html("");

        var indexString;
        // create new ones
        if (dataSources.length) {
          _(dataSources).each(
            function (dataSource, index) {
              if (index === 0) indexString = "";
              else if (index === 1) indexString = "2";
              else if (index === 2) indexString = "3";
              else indexString = "4";

              var $container = this.$content.find(".filters" + indexString);
              var fmv = new FilterViews.FilterMenusView({
                parentView: this,
                el: $container,
                model: dataSource,
              });
              this.filterMenusViews.push(fmv);
              this.repeatEventsFrom(fmv);
            }.bind(this)
          );
        }
      },
      createDemoCatsView: function () {
        var filterSources = this.dataBoxView.getFilterSources();
        var dataSource = filterSources.demographicCategories;
        var $container = this.$content.find(".demoCats");
        var defaults = {
          viewTypeName: "DemographicCategorySelectorView",
        };
        var DemoCatsViewType, options;
        var demoCatsOptions = PluginHelper.getCurrentPluginDemoCatsOptions();

        options = _.extend(defaults, demoCatsOptions);

        DemoCatsViewType = FilterViews[options.viewTypeName]
          ? FilterViews[options.viewTypeName]
          : FilterViews.DemographicCategorySelectorView;

        if (this.demoCatsView) {
          this.demoCatsView.destroy();
          delete this.demoCatsView;
        }

        if (dataSource) {
          this.demoCatsView = new DemoCatsViewType({
            parentView: this,
            el: $container,
            model: filterSources.demographicCategories,
            showGrades: options.showGrades,
          });
          this.repeatEventsFrom(this.demoCatsView);
        } else {
          $container.html("");
        }
      },
      createDebugToolbar: function () {
        if (window.debugEnabled) {
          var buttons = {};
          if (this.dataBoxView.getFilterSources().filters) {
            buttons["Raw Filter JSON"] = function () {
              return this.dataBoxView.getFilterSources().filters;
            }.bind(this);
            buttons["Processed Default Filter JSON"] = function () {
              return this.dataBoxView
                .getFilterSources()
                .filters.getDefaultMenus();
            }.bind(this);
          }
          if (this.dataBoxView.getFilterSources().demographicCategories) {
            buttons["Demographics JSON"] = function () {
              return this.dataBoxView.getFilterSources().demographicCategories;
            }.bind(this);
          }

          buttons["Typ. School JSON"] =
            CommonDataSources.Derivative.orgFinderTypeaheadSchools ||
            this.dataBoxView.getFilterSources().orgs.unfilteredSchools;
          buttons["Typ. Group JSON"] =
            CommonDataSources.Derivative.orgFinderTypeaheadDistricts ||
            this.dataBoxView.getFilterSources().orgs.unfilteredGroups;
          buttons["Org JSON"] =
            CommonDataSources.entities ||
            this.dataBoxView.getFilterSources().orgs;

          // (maybe) destroy and create the new toolbar
          if (this.debugToolbar) {
            this.debugToolbar.destroy();
          }
          this.debugToolbar = new Views.DebugToolbar(buttons);
          this.$content.prepend(this.debugToolbar.$el);
        }
      },

      /** do anything necessary to update the config view when the
       * report changes */
      update: function () {
        // we need to tear down and re-render the
        // filters and demographic categories views.
        this.orgFinderView.render();
        this.createFilterMenusViews();
        this.createDemoCatsView();
        this.createDebugToolbar();
      },

      /** WF-specific: take a parameter object, created by getParams, and
       * process the nested array created by the multiselect checkboxes into
       * the flat hash expected by WebFOCUS services.
       * WARNING: this probably has the side effect of altering the array we
       * pass into it. it would be nice to rewrite this without that.
       * @param {Object} params the serialized parameter object
       * @returns {Object} the params passed to us, with its nested array of
       * checkbox values flattened into CATEGORY# properties */
      flattenCategoryCheckboxArrays: function (params) {
        var categories,
          categoriesHash = {};

        _(params).each(function (value, name) {
          if (_(value).isArray()) {
            categories = value;
            delete params[name];
          }
        });

        if (categories) {
          _(categories).each(function (category, i) {
            categoriesHash["CATEGORY" + (i + 1)] = category;
          });
          return _(params).extend(categoriesHash).value();
        } else {
          return params;
        }
      },
      setOrg: function (orgObject) {
        if (this.orgFinderView) {
          this.orgFinderView.setOrg(orgObject);
        }
      },
      setFY: function (fY) {
        if (this.orgFinderView) {
          this.orgFinderView.setFY(fY);
        }
      },
      setEconDevReg: function (edr) {
        if (this.orgFinderView) {
          this.orgFinderView.setEconDevReg(edr);
        }
      },

      //
      // INFO
      //

      /** create a hash of parameters from the current state of the UI,
       * prepared and ready for passing to ajax calls */
      getParams: function () {
        var serialized = this.getSelections(),
          defaultParams = this.dataBoxView.getFilterSources().filters
            ? this.dataBoxView.getFilterSources().filters.defaultParams
            : {},
          withDefaults = _.defaults(serialized, defaultParams),
          noEmpties = _.omit(withDefaults, function (value) {
            return value === "";
          });

        return noEmpties;
      },
      /** return the datum of the selected org from the org finder */
      getOrg: function () {
        if (this.orgFinderView) {
          return this.orgFinderView.getOrg();
        } else {
          return null;
        }
      },
      /** return the raw state of the form UI's selections
       * @returns {Object} the state of the config form */
      getSelections: function () {
        return this.$("form").serializeObject();
      },
      getFilterElement: function (name) {
        return this.$el.find("[name=" + name + "]");
      },
      getFilterValue: function (name) {
        return this.getFilterElement(name).val();
      },
      _filterSummaryIsForced: function (filterCode) {
        try {
          var forcedSummaryFilters =
            PluginHelper.getCurrentPlugin().options.forcedSummaryFilters;
          if (forcedSummaryFilters) {
            return _(forcedSummaryFilters).contains(filterCode);
          }
        } catch (ex) {
          console.log("caught " + ex);
        }

        return false;
      },
      /** produce the summary object that's used to place information in the
       * title bar/toggle of the data box.
       * @todo decouple this from the view HTML. I just changed the HTML for
       * filter labels and this function broke.
       * @returns {Object} */
      getSummaryObject: function () {
        if (!this.orgFinderView) {
          return null;
        }
        var that = this;
        var showAll = false,
          $democats,
          categoryEntries = [],
          categoryText,
          $fields = this.$content,
          org = this.orgFinderView.getOrg(),
          summary = {
            orgName: org ? org.name : "Statewide ", // cheating!! this is evil!
            parentName: org ? org.parentName : "",
            entries: [],
          };

        // do the filter menus
        // var tempLabel;
        /* $fields.find("input").each(function () {
            var $field = $(this),
            value = $field.val();
           
            if (this.name === "ECO_DEV_REGION" && (value != "FOC_NONE" || value !== "")) {
                summary.orgName = $field.value;
              } else if (this.name === "ECODEVREGION" && (value != "FOC_NONE" && value !== "")) {

                summary.orgName = value;
              }
              console.log(summary.orgName);
        }); */
var that = this;
        $fields.find("select").each(function () {
          var $field = $(this),
            value = $field.val(),
            selectedDisplayName = $field
              .children("option[value='" + value + "']")
              .html(),
            defaultValue = $field.children("option").first().attr("value"),
            label = $fields.find("label[for=" + $field.attr("id") + "]").html();

          if (this.name === "ECO_DEV_REGION" && value != "FOC_NONE") {
            that.orgFinderView.setQuery("Statewide");
            if(that.orgFinderView.chosenOrg.name != "Statewide"){
                Backbone.trigger("map:orgReset", {
                    requestingDataBoxView: that.orgFinderView.options.parentView.dataBoxView
                });
            }
            summary.orgName = $field.value;
            that.orgFinderView.clearField();
          } else if (this.name === "ECODEVREGION" && value != "FOC_NONE") {
            that.orgFinderView.setQuery("Statewide");
            if(that.orgFinderView.chosenOrg.name != "Statewide"){
                Backbone.trigger("map:orgReset", {
                    requestingDataBoxView: that.orgFinderView.options.parentView.dataBoxView
                });
            }
            summary.orgName = selectedDisplayName;
            that.orgFinderView.clearField();
            
          }
          if ((this.name === "COLLEGE_STATE" || this.name === "INSTITUTION_TYPE" ||this.name === "COLLEGE_SELECTIVITY") && value != "FOC_NONE") {

            that.orgFinderView.setQuery("Statewide");
            if(that.orgFinderView.chosenOrg.name != "Statewide"){
                Backbone.trigger("map:orgReset", {
                    requestingDataBoxView: that.orgFinderView.options.parentView.dataBoxView
                });
            }
            summary.orgName = selectedDisplayName;
            that.orgFinderView.clearField();

          } 
          //CHARBY: We need to add some abreviations to the label string.
          //This is ugly, but the alternative is changing the data structure
          //to include display name
          if (~label.indexOf("High School")) {
            var pos = label.indexOf("High School") + 12; //num of chars
            label = [label.slice(0, pos) + "(HS) " + label.slice(pos)].join();
          }

          if (
            (showAll === true ||
              value !== defaultValue ||
              that._filterSummaryIsForced($field.attr("name"))) &&
            selectedDisplayName
          ) {
            if (window.location.href.indexOf("rc.education.mn.gov") > 0)
              summary.entries.push(
                "<span class='label msg_" +
                  $field.attr("id") +
                  "'>" +
                  $.trim(label) +
                  ":</span> " +
                  "<span class='msg_" +
                  selectedDisplayName +
                  "'>" +
                  selectedDisplayName +
                  "</span>"
              );
            else
              summary.entries.push(
                "<span class='label'>" +
                  $.trim(label) +
                  ": </span> <span>" +
                  selectedDisplayName +
                  "</span>"
              );
          }
        });

        // do the demo cats
        $democats = $fields.find("input[type=checkbox]:checked");
        if ($democats.length) {
          $democats.each(function () {
            var label = $.trim($(this).siblings("label").text());
            categoryEntries.push(label);
          });
          categoryText =
            "<span class='label'>Demographics</span>: " +
            categoryEntries.join(" ");
          summary.entries.push(categoryText);
        }
        return summary;
      },
      heightAddedSinceOpen: function () {
        var originalHeight = -parseInt(this.$heightBox.css("top"), 0),
          diff = this.$heightBox.height() - originalHeight + this.extraPixels;
        return diff;
      },
      /** determine if this data box has any filter menus */
      hasFilterMenus: function () {
        if (_.isArray(this.filterMenusViews)) {
          return !!this.filterMenusViews.length;
        } else {
          return !!this.filterMenusView;
        }
      },
      /** determine if this data box has any demographic category selector */
      hasDemoCats: function () {
        return !!this.demoCatsView;
      },

      //
      // EVENT HANDLERS
      //

      /** pane toggle event handler.  */
      onTogglePressed: function (event) {
        if (event.keyCode && event.keyCode != 13) {
          return;
        }
        if (event.keyCode && event.delegateTarget.className != "handle") {
          return;
        }
        if (this.$el.is(".closed, .closing")) {
          this.open();
        } else {
          this.close();
        }
      },
      onToggleTouchEnd: function (/*event*/) {
        this.activatedByTouch = true;
      },
      onExpanderTransitionEnd: function (event) {
        // assume we are bound to this object, not the element
        var $drawer = this.$el;

        if ($drawer.is(".closing")) {
          $drawer.removeClass("closing").addClass("closed");
          if (this.method === "translate3d") {
            this.setInitialTopPosition();
          }
          $drawer.attr("hidden", true);
          $drawer.attr("aria-hidden", true);
          this.trigger("drawerClosed");
        } else {
          this.trigger("drawerOpened");
        }
        if (event) {
          event.preventDefault();
        }
      },
      onEscKeyPressed: function () {
        this.close();
      },

      // NOTE: Auto-focus of the typeahead has moved into the synchronous
      // DataBoxView.beforeDrawerOpen() event handler, triggered by
      // ConfigDrawerView.open() so that Safari/iOS allows it to actually
      // work. http://stackoverflow.com/a/7332160/446564
      // this.orgFinderView.$el.find(".orgNameClearButton").click();

      // onDrawerOpened: function () {
      // 	// NOTE: this awful browser detection should be replaced with clean
      // 	// Bowser queries once this pull request lands:
      // 	// https://github.com/ded/bowser/pull/34
      // 	if (!Bowser.ios &&
      // 		!(/android/i.test(window.navigator.userAgent) && !Bowser.chrome)) {
      // 		// TODO: finish investigating WHY this won't work on iOS. Even
      // 		// if I just clear the field, it needs two real taps to get the
      // 		// control focus. WAT
      // 		this.orgFinderView.clearAndFocusTypeahead();
      // 	}
      // },

      // PROXY INTERFACES

      // these are just to hide the internal implementation from
      // outside callers (like the controller)

      /** proxy function to org finder */
      splitOrgID: function () {
        this.orgFinderView.splitOrgID();
      },

      //
      // ACTIONS
      //

      setFilterState: function (selectionStateToRestore) {
        this.updateFilterMenus(selectionStateToRestore);
        this.updateDemoCats(selectionStateToRestore);
      },

      updateFilterMenus: function (selectionStateToRestore) {
        if (this.hasFilterMenus()) {
          _(this.filterMenusViews).invoke(
            "updateMenus",
            selectionStateToRestore
          );
        }
      },
      updateDemoCats: function (selectionStateToRestore) {
        if (this.hasDemoCats()) {
          this.demoCatsView.updateSelections(selectionStateToRestore);
        }
      },

      setFilterValue: function (name, value) {
        var $filter = this.getFilterElement(name);
        $filter.val(value);
      },

      open: function () {
        var boxHeight = this.$heightBox.height();

        // close the bubble
        Backbone.trigger("bubblesShouldClose");

        // get ready to animate
        this.setInitialTopPosition();

        // clean up any previous activity
        clearTimeout(this.timeout);
        this.$el.removeAttr("hidden");
        this.$el.removeAttr("aria-hidden");
        this.$el.removeClass("closed closing").addClass("open");

        if (this.method === "translate3d") {
          this.$heightBox.css({
            transform:
              "translate3d(0," + (boxHeight + this.extraPixels) + "px,0)",
            "-webkit-transform":
              "translate3d(0," + (boxHeight + this.extraPixels) + "px,0)",
          });
        } else {
          this.$heightBox.animate(
            {
              top: "0px",
            },
            this.animationDuration
          );
        }

        // this is how we fake a transitionend event. hah!
        // WARNING: this is hard-coded to make sense for a transition
        // duration of 200ms, which is set in CSS.
        this.timeout = setTimeout(this.onExpanderTransitionEnd.bind(this), 250);

        // clear the typeahead so it shows the "type here..." message
        if (this.activatedByTouch) {
          this.orgFinderView.clearField();
          delete this.activatedByTouch;
        } else {
          this.orgFinderView.clearAndFocusTypeahead();
        }

        // let other objects know we're going to open
        this.trigger("beforeDrawerOpen");
      },
      close: function () {
        // let other objects know we're going to close
        this.trigger("beforeDrawerClose");

        // clean up any previous activity
        clearTimeout(this.timeout);
        this.orgFinderView.restoreQuery();
        this.orgFinderView.closeSuggestions();
        this.$el.find("input").blur();
        this.$el.removeClass("open").addClass("closing");

        // using the proper method, slide the drawer up
        if (this.method === "translate3d") {
          this.$heightBox.css({
            transform:
              "translate3d(0px," + -this.heightAddedSinceOpen() + "px,0px)",
            "-webkit-transform":
              "translate3d(0," + -this.heightAddedSinceOpen() + "px,0)",
          });
        } else {
          this.setInitialTopPosition(true);
        }

        // clean up the activatedByTouch flag
        delete this.activatedByTouch;

        // Focus the first pane heading opener thing
        //                this.dataBoxView.$el.find('h4.ui-collapsible-heading a').eq(0).focus();

        // this is how we fake a transitionend event. hah!
        // WARNING: this is hard-coded to make sense for an
        // transition duration of 300ms, which is set in CSS.
        this.timeout = setTimeout(this.onExpanderTransitionEnd.bind(this), 400);
      },
      setInitialTopPosition: function (animate) {
        var boxHeight = this.$heightBox.height(),
          properties = {
            top: -boxHeight - this.extraPixels + "px",
          };

        if (animate) {
          this.$heightBox.animate(properties, this.animationDuration);
        } else {
          this.$heightBox.css(properties);
        }
      },
      hideFiltersAndDemoCats: function () {
        if (this.hasFilterMenus()) {
          _(this.filterMenusViews).invoke("hide");
        }
        if (this.demoCatsView) {
          this.demoCatsView.hide();
        }
      },
      showFiltersAndDemoCats: function () {
        if (this.hasFilterMenus()) {
          _(this.filterMenusViews).invoke("show");
        }
        if (this.demoCatsView) {
          this.demoCatsView.show();
        }
      },
    }
  );

  /**
   * @class module:cardboard/databoxes.DataBoxPaneView
   * @augments Views.BaseView
   * @classdesc This renders a single pane inside a data box. It's
   * responsible for rendering itself, and probably some
   * controller-ish duties, for simplicity
   */

  DataBoxes.DataBoxPaneView = Views.BaseView.extend(
    /** @lends module:cardboard/databoxes.DataBoxPaneView.prototype */ {
      model: null,

      /** the HTML template string we use to construct an
       * accordion pane. this gets replaced by an element maker
       * function at runtime.
       * @type String */

      // pre-enhanced version
      // wrapperTemplate: "<div class='pane ui-collapsible-collapsed' data-enhanced='true' data-role='collapsible' data-inset='false'>"
      // 	+ "<h4 class='ui-collapsible-heading ui-collapsible-heading-collapsed'><a class='ui-collapsible-heading-toggle ui-btn ui-btn-icon-left ui-btn-inherit ui-icon-plus' href='#'><span class='title'>{{title}}</span></a></h4><div class='ui-collapsible-content ui-body-inherit ui-collapsible-content-collapsed'></div></div>",

      // unenhanced version
      wrapperTemplate: dataBoxPaneTemplate,

      infoButtonTemplate:
        "<a class='infoButton' title='click for more information'><img src='/images/circle-i.svg' alt='more information icon' /></a>",

      /** declarative events. empty for now
       * @type Object */
      events: {
        collapsibleexpand: "onOpen",
        collapsiblecollapse: "onClose",
        "click .infoButton": "onInfoButtonClicked",
        //                "keydown .infoButton": "onInfoButtonClicked",
        "touchend .infoButton": "onInfoButtonClicked",
      },

      /** constructor
             * @param {Object} options the config object for the pane, which
             * should come from the report plugin
             * @param {Object} dataBox the parent dataBox that contains this pane
             * @example
             {
            	 title: "1929 Student Achievement Level",
            	 views: [
            		 {
            			 type: "stackedColumn",
            			 dataSourceName: "data"
            		 }
            	 ]
             }
             */
      initialize: function (options, dataBox) {
        /** array of view objects in this pane
         * @type Array */
        this.views = [];

        this.options = options;

        if (_.isUndefined(options.title) && _.isUndefined(options.titlePath)) {
          console.log(
            "WARNING: No title on this pane. Did you maybe put a view in the panes bucket?"
          );
          console.log("Here's the offending pane:");
          console.log(options);
        }

        /** the parent databox view
         * @type {Object} */
        this.dataBoxView = dataBox;

        //if (this.options.titlePath) {
        //   this.options.title = this.getDynamicContent(this.options.titlePath);
        //  }

        this.titleTemplate = Hogan.compile(this.options.title);
      },

      /** destructor! unwire all necessary events, and deallocate
       * other resources as necessary */
      destroy: function () {
        this.destroyContents();
        this.undelegateEvents();
        this.stopListening();
        this.$el.remove();
      },
      destroyContents: function () {
        Util.Data.popEach(this.views, function (view) {
          if (_.isFunction(view.destroy)) {
            view.destroy();
          }
        });
        if (this.debugToolbar) {
          this.debugToolbar.destroy();
        }
      },

      /** render the container -- clickable header and container
       * box. this should only get called when constructing each
       * pane view all at once. contents are rendered spearately,
       * when the pane is opened.
       * @returns {Object} this */
      render: function (openByDefault, paneToReplace) {
        var isErrorPane = this.options.type === "errorPane",
          paneDataSources = this.getPaneDataSources(),
          allSourcesLoaded = _.every(paneDataSources, function (source) {
            return source ? source.isLoaded() : true;
          });

        if (isErrorPane) {
          openByDefault = true;
        }

        this.createWrapperElement({
          title: "Loading...",
          closed: !openByDefault,
          infoButton:
            !!this.options.explanationText || !!this.options.explanationTextPath
              ? true
              : false,
        });

        // save reference to content for convenience
        this.$content = this.$el
          .find(".ui-collapsible-content, .content")
          .first();

        // if this is an error pane, we have some things to do.
        if (isErrorPane) {
          this.$el.addClass("errorPane");
        } else {
          // if all of the data sources in this pane have loaded already,
          // let's update the title.
          if (allSourcesLoaded) {
            this.updateTitle();
          }
        }

        // it might not want to show a toggle
        if (this.options.toggleHidden) {
          this.$el.addClass("toggleHidden");
        }

        // when all the needed data sources are ready, update the pane
        // title, and listen for data syncs to do the same
        var yearsDataSource = this.getYearsDataSource();
        if (yearsDataSource) {
          this.listenTo(
            yearsDataSource,
            "sync",
            this.updateTitleWhenReady.bind(this)
          );
        }
        if (!yearsDataSource || yearsDataSource.isLoaded()) {
          this.updateTitleWhenReady();
        }

        // add ourselves to the DOM
        this.dataBoxView.addToContent(this.$el, paneToReplace);

        // now that it's placed, Highcharts can safely determine the correct
        // chart width.
        if (openByDefault) {
          this.renderContents();
        }

        // attach events
        this.delegateEvents();

        // make it collapsible, but should it be open by default?
        this.suppressPaneEvents = true;
        this.$el.collapsible({
          collapsed: !openByDefault,
        });
        this.suppressPaneEvents = false;
        return this;
      },

      createDebugToolbar: function () {
        if (window.debugEnabled) {
          var paneDataSources = this.getPaneDataSources(),
            options = {};

          // charting data buttons
          _(this.getChartViews()).each(function (chartView, index) {
            var buttonName =
              "Chart Config JSON" + (index > 1 ? " " + index : "");
            options[buttonName] = chartView.getChartConfig.bind(chartView);
            buttonName = "Chart Series JSON" + (index > 1 ? " " + index : "");
            options[buttonName] = chartView.getChartableJSON.bind(chartView);
          });

          // data source buttons
          _(paneDataSources).each(function (dataSource, name) {
            options[_s.humanize(name) + " JSON"] = function () {
              return dataSource.toJSON();
            };
          });

          if (this.isErrorPane()) {
            var allDataSources = this.dataBoxView.getDataSources();
            _(allDataSources).each(function (dataSource, name) {
              options[_s.humanize(name) + " JSON"] = function () {
                return dataSource.toJSON();
              };
            });
          }

          if (_.keys(options).length > 0) {
            this.debugToolbar = new Views.DebugToolbar(options);
            this.$content.prepend(this.debugToolbar.$el);
          }
        }
      },

      /** loop over each view in the pane info object and either
       * make our view objects, or update their contents, if the
       * pane has already been rendered!
       * @param {Boolean} force - force re-rendering of a pane's content, even
       * if it's already been rendered. this is used by
       * setDataErrorViewState
       * @param {Object} renderOptions - a hash of options of be passed along
       * to all view objects. this was created to allow situational disabling
       * of chart animations. */
      renderContents: function (force, renderOptions) {
        if (this.alreadyRendered() && !force) {
          return Util.bottomObscuredByScroll(this.$el);
        }
        if (force) {
          this.destroyContents();
        }

        _(this.options.views).each(function (viewInfo) {
          // Create this view
          var viewObject = this.createSingleView(viewInfo);

          // keep track of it, add its wrapper element to the
          // DOM, and render it
          this.views.push(viewObject);
          this.addToContent(viewObject.$el);

          // if we need to scroll to make the parent pane visible, don't
          // animate the chart
          if (Util.bottomObscuredByScroll(this.$el)) {
            renderOptions = _.extend(renderOptions, {
              animate: false,
            });
          }

          if (viewObject instanceof Charts.ChartView) {
            _.defer(function () {
              viewObject.render(renderOptions);
            });
          } else {
            viewObject.render(renderOptions);
          }
        }, this);

        // maybe create a debug toolbar
        this.createDebugToolbar();

        // mark it rendered
        this.markRendered();

        return Util.bottomObscuredByScroll(this.$el);
      },
      createSingleView: function (viewInfo) {
        var viewObject,
          model = this.dataBoxView.getDataSources()[viewInfo.dataSourceName],
          viewConfig = {
            model: model,
            type: viewInfo.type,
            viewInfo: viewInfo,
            dataBoxView: this.dataBoxView,
          };

        // wait: special case here. is this for rendering an error message?
        if (this.options.type === "errorPane") {
          // replace the default, probably awful, error message with one
          // supplied by the plugin
          var pluginMessages = PluginHelper.getCurrentPlugin().errorMessages,
            betterErrors = _(this.dataBoxView.dataErrors)
              .map(function (errorObject) {
                var newMessage =
                  (pluginMessages && pluginMessages[errorObject.type]) ||
                  errorObject.message;
                return _.extend({}, errorObject, {
                  message: newMessage,
                });
              })
              .value();

          viewConfig.model = new Collections.Collection(betterErrors);
        }

        // what kind of view is this? create it
        if (viewInfo.type === "template") {
          viewObject = new Views.TemplateView(viewConfig);
        } else {
          // a chart!
          viewObject = new Charts.ChartView(viewConfig);
        }
        return viewObject;
      },

      /** This is to be able to put the year, and other things, in each title
       * bar once our data is loaded up. affects only the titles in the
       * toggles bars.
       *
       * NOTE: this used to extract the year just from the "data" data source,
       * but since now we can have multiple data sources with different names,
       * the policy is to extract the year from the first data source that
       * specifies a yearPath. We also add filter state to the available data
       * environment.
       * Therefore, this should only be called once all filters and data
       * sources have loaded. */
      updateTitle: function (noData) {
        // we support jqm 1.3 and 1.4, respectively
        var $el = this.$("h4 .ui-btn-text, .title");
        // if we've been told there's no backing data, it's not a template,
        // and we can render immediately
        // if (this.options.titlePath) {
        //    this.setDynamicContentDataString(this.options.titlePath, $el);
        // }

        if (noData) {
          $el.html(this.options.title);
          this.androidTitleRenderingWorkaround();
          return;
        }

        var dataBox = this.dataBoxView,
          availableData = {
            year: "",
            lastYear: "",
            filters: dataBox.getConfigParams(),
            dataSources: _(dataBox.getDataSources())
              .pairs()
              .map(function (pair) {
                return [pair[0], pair[1].toJSON()];
              })
              .object()
              .value(),
          },
          yearsDataSource = this.getYearsDataSource();

        // if there's a data source that gives us years, or a range of
        // years, let's add that to the available data environment
        if (yearsDataSource) {
          if (_.isFunction(yearsDataSource.getYearString)) {
            availableData.year = yearsDataSource.getYearString(
              this.options.yearPath
            );
          }
          if (_.isFunction(yearsDataSource.getLastYearString)) {
            availableData.lastYear = yearsDataSource.getLastYearString(
              this.options.lastYearPath
            );
          }
        }
        $el.html(this.titleTemplate.render(availableData));
        this.androidTitleRenderingWorkaround();
      },
      androidTitleRenderingWorkaround: function () {
        var $el = this.$("h4 .ui-btn-text, h4 .title");

        if (Bowser.android && !Bowser.chrome && !Bowser.firefox) {
          $el.css({
            position: "relative",
          });
        }
      },

      updateTitleWhenReady: function () {
        var title = this.options.title,
          allDataSources = this.dataBoxView.getDataSources(),
          yearsDataSource,
          dependencies = [],
          promisesArray;

        // if there are no mustache templates in the title, we have no data
        // to insert, and can render immediately!
        if (title.indexOf("{{") === -1) {
          this.updateTitle(true);
          return;
        } else {
          yearsDataSource = this.getYearsDataSource();
          dependencies.push(yearsDataSource);
        }

        // wait for filters source, if there is one
        if (
          allDataSources.filters &&
          allDataSources.filters.requestDonePromise
        ) {
          dependencies.push(allDataSources.filters);
        }
        // wait for filters2 source, if there is one
        if (
          allDataSources.filters2 &&
          allDataSources.filters2.requestDonePromise
        ) {
          dependencies.push(allDataSources.filters2);
        }
        // wait for filters2 source, if there is one
        if (
          allDataSources.filters3 &&
          allDataSources.filters3.requestDonePromise
        ) {
          dependencies.push(allDataSources.filters3);
        }
        if (
          allDataSources.filters4 &&
          allDataSources.filters4.requestDonePromise
        ) {
          dependencies.push(allDataSources.filters4);
        }

        promisesArray = _(allDataSources)
          .pluck("requestDonePromise")
          .compact()
          .value();

        Promise.all(promisesArray).then(
          function () {
            this.updateTitle(false);
          }.bind(this)
        );
      },

      /** loop over each view in the pane info object and demand
             that they update their contents if necessary! */
      updateContents: function () {
        _(this.views).each(
          function (view) {
            view.render();
          }.bind(this)
        );
      },

      /** add some new thing to our content div
       * @param {Element | jQuery} passedNewThing the thing to add */
      addToContent: function (passedNewThing) {
        var newThing = $(passedNewThing);
        this.$content.append(newThing);
      },

      /** mask all data views in this pane */
      maskViewsForLoading: function () {
        _(this.views).invoke("maskForLoading");
      },
      open: function () {
        this.suppressPaneEvents = true;
        this.$el.collapsible("expand");
        this.suppressPaneEvents = false;
      },
      close: function () {
        // we never want to close an error pane. never.
        if (this.isErrorPane()) {
          return;
        }
        this.suppressPaneEvents = true;
        this.$el.collapsible("collapse");
        this.suppressPaneEvents = false;
      },

      //
      // INFO
      //

      /** @returns {Object} the config object that was passed to the pane to
       * set it up when it was created */
      getConfigObject: function () {
        return this.options;
      },

      /** @returns {Boolean} whether or not the pane is currently open
       * (non-collapsed) */
      isOpen: function () {
        // return !this.$el.collapsible("option", "collapsed");
        return !this.$el.is(".ui-collapsible-collapsed");
      },
      isErrorPane: function () {
        return this.options.type === "errorPane";
      },

      /** @returns {Array}  */
      getChartViews: function () {
        return _.filter(this.views, function (view) {
          return view instanceof Charts.ChartView;
        });
      },

      /** @returns {Array} an array of the names of all data sources rendered
       * in this pane */
      getPaneDataSourceNames: function () {
        return _(this.options.views)
          .filter(function (view) {
            return _.has(view, "dataSourceName");
          })
          .pluck("dataSourceName")
          .uniq()
          .value();
      },

      /** @returns {Object} a hash of every data model object in use by this
       * frame. the hash keys are the data source names as they appear in the
       * report plugin */
      getPaneDataSources: function () {
        var names = this.getPaneDataSourceNames();
        return _(this.dataBoxView.getDataSources()).pick(names).value();
      },

      /** @returns {Object} whichever data source we're using to pull years from. First,
       * look in this pane. if we can't find on there, look at all data
       * sources. */
      getYearsDataSource: function () {
        var source = this.getPaneYearsDataSource();
        if (!source) {
          source = this.getDataBoxYearsDataSource();
        }
        return source;
      },
      getDataBoxYearsDataSource: function () {
        return _(this.dataBoxView.getDataSources()).find(function (source) {
          return source.getDefinition().yearPath;
        });
      },
      getPaneYearsDataSource: function () {
        return _(this.getPaneDataSources())
          .toArray()
          .find(function (source) {
            return source.getDefinition().yearPath;
          });
      },

      //
      // EVENT HANDLERS
      //

      /** pane open event handler. catches jQuery's event.
       * @fires DataBoxPaneView#paneOpened */
      onOpen: function () {
        // first param being false means this thing will be smart enough to
        // check if it's already been rendered before it runs off and
        // rebuilds everything
        var necessaryScroll = this.renderContents(false);

        // fire our own event, post-render.  the main workspace, catches
        // this, at least, because we may need to ask it to scroll the
        // page. It also needs to update the URL.
        if (!this.suppressPaneEvents) {
          Backbone.trigger("paneOpened", {
            paneView: this,
            necessaryScroll: necessaryScroll,
          });
        }

        if (
          this.dataBoxView.controller.dataSources.data.url.indexOf(
            "stayingInAndCompletingCollege"
          ) > -1 ||
          this.dataBoxView.controller.dataSources.data.url.indexOf(
            "collegeActivity"
          ) > -1 ||
          this.dataBoxView.controller.dataSources.data.url.indexOf(
            "deveducation"
          ) > -1 ||
          this.dataBoxView.controller.dataSources.data.url.indexOf(
            "newcollegestudentdemographics"
          ) > -1 ||
          this.dataBoxView.controller.dataSources.data.url.indexOf(
            "newcollegestudentenrollment"
          ) > -1 ||
          this.dataBoxView.controller.dataSources.data.url.indexOf(
            "collegegraduates"
          ) > -1 ||
          this.dataBoxView.controller.dataSources.data.url.indexOf(
            "HSGraduatesToEmployment"
          ) > -1 ||
          this.dataBoxView.controller.dataSources.data.url.indexOf("PSXfer") >
            -1
        ) {
          if (
            this.$el[0].children[2].children[0].children[0].innerHTML.indexOf(
              "Download All Data"
            ) > -1
          ) {
            var params = this.dataBoxView.controller.dataSources.data.params;
            var categories,
              categoriesHash = {};

            _(params).each(function (value, name) {
              if (_(value).isArray()) {
                categories = value;
                delete params[name];
              }
            });

            if (categories) {
              _(categories).each(function (category, i) {
                if (i == 0) categoriesHash["categories"] = category;
                else categoriesHash["categories"] += "|" + category;
              });
              params = _(params).extend(categoriesHash).value();
            }

            var string =
              "<p><a href='" +
              this.dataBoxView.controller.dataSources.data.url +
              "&" +
              jQuery.param(params) +
              "&outputFormat=XLSX' data-role='button' target='_blank' class='ui-link ui-btn ui-shadow ui-corner-all' role='button'>Download All Data</a></p>";

            if (
              this.dataBoxView.controller.dataSources.data.url.indexOf(
                "stayingInAndCompletingCollege"
              ) > -1 ||
              this.dataBoxView.controller.dataSources.data.url.indexOf(
                "collegeActivity"
              ) > -1
            ) {
              string = string.replace("orgId", "HSGRAD_ORGID");
            } else if (
              this.dataBoxView.controller.dataSources.data.url.indexOf(
                "newcollegestudentdemographics"
              ) > -1 ||
              this.dataBoxView.controller.dataSources.data.url.indexOf(
                "PSXfer"
              ) > -1 ||
              this.dataBoxView.controller.dataSources.data.url.indexOf(
                "newcollegestudentenrollment"
              ) > -1 ||
              this.dataBoxView.controller.dataSources.data.url.indexOf(
                "collegegraduates"
              ) > -1
            ) {
              string = string.replace("orgId", "PS_ORGID");
            }
            this.$el[0].children[2].children[0].children[0].innerHTML = string;
          }
        }
      },
      /** pane open event handler. catches jQuery's event.
       * @fires DataBoxPaneView#paneopened */
      onClose: function () {
        // fire our own event, post-render.  the main workspace, catches
        // this, at least, because it needs to refresh the URL.
        if (!this.suppressPaneEvents) {
          Backbone.trigger("paneClosed", {
            paneView: this,
          });
        }
      },
      /** catch a click on the optional info button */
      onInfoButtonClicked: function (e) {
        e.preventDefault();
        e.stopPropagation();
        /* if (!!this.options.explanationTextPath) {
                     window.workspace.setModalInfoDialogDynamicPath(this.options.explanationTextPath);
                     //window.workspace.setModalInfoDialogContent(this.getDynamicContent(this.options.explanationTextPath));
                 } else {*/

        window.workspace.setModalInfoDialogContent(
          this.options.explanationText
        );
        Backbone.trigger("modalOpened");
        // }
        window.workspace.showModalInfoDialog();
        setTimeout(function () {
          if (window.selectedLang != "en") {
            changeLang(window.selectedLang);
          }
        }, 1500);
      },
    }
  );

  return DataBoxes;
});

